import { useCallback, useEffect, useState } from 'react'

type Theme = 'light' | 'dark'

const STORAGE_KEY = 'theme'

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark'
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  const prefersLight =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: light)').matches
  return prefersLight ? 'light' : 'dark'
}

/**
 * The "CW" tab icon, colours matching the given theme. A static favicon.svg's
 * prefers-color-scheme only tracks the OS, so we swap it here to follow the
 * in-site toggle exactly (light => light bg/dark letters, dark => inverted).
 */
function faviconDataUri(theme: Theme): string {
  const bg = theme === 'dark' ? '#0b0d10' : '#f4f3ef'
  const fg = theme === 'dark' ? '#f4f3ef' : '#0b0d10'
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<rect width="64" height="64" rx="14" fill="${bg}"/>` +
    `<text x="32" y="44" text-anchor="middle" ` +
    `font-family="Inter,system-ui,-apple-system,sans-serif" font-size="34" ` +
    `font-weight="800" fill="${fg}">CW</text></svg>`
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

function applyFavicon(theme: Theme) {
  if (typeof document === 'undefined') return
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.type = 'image/svg+xml'
  link.href = faviconDataUri(theme)
}

/** Persisted light/dark theme, applied by toggling `.dark` on <html>. */
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme)

  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle('dark', theme === 'dark')
    localStorage.setItem(STORAGE_KEY, theme)
    applyFavicon(theme)
  }, [theme])

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'))
  }, [])

  return { theme, toggleTheme }
}
